import {
	callSessionStatesAllowingTransfer,
	callSessionStatesDisablingCallButton,
	namesOf,
} from "@/common/helpers";
import { ContactListItem, ContactType } from "@/repository/models";
import {
	Button,
	MenuItem,
	MenuList,
	PopoverProps,
} from "@fluentui/react-components";
import {
	Call20Regular,
	Phone20Regular,
	BookContacts20Regular,
	Calendar20Regular,
	People20Regular,
	CallForward20Regular,
} from "@fluentui/react-icons";
import { useMemo } from "react";
import { CallMenuItemsProps } from "./types";
import { useIntl } from "react-intl";
import { useMenuItemStyle, useStyle } from "./style";
import { CallTargetType, TransferType } from "@/common/types";
import { MenuView } from "@/components/atoms/menu";
import { PopoverView } from "@/components/atoms/popover";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { AppSettingsState } from "@/common/stores/persistingStores/app";

export const CallMenuItems = (props: CallMenuItemsProps) => {
	const {
		contact,
		isOutboundPossible,
		isOutboundAllowed,
		hasAnyOutboundRightEndpoints,
		onCallClick,
		onTransferClick,
		onAgendaClick,
		callSession,
		isCallMenuOpened,
		setIsCallMenuOpened,
	} = props;

	const classes = useStyle();
	const menuItemClass = useMenuItemStyle();
	const isAgendaEnabled = false;
	const intl = useIntl();
	const contactListItemPropertyNames = namesOf<ContactListItem>();

	const handleOpenChange: PopoverProps["onOpenChange"] = (_, data) => {
		setIsCallMenuOpened(data.open || false);
	};

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	const contactNotHavingCallableInformation = (contact: ContactListItem) => {
		const result = !(
			contact.businessPhone ||
			contact.mobilePhone ||
			contact.userPrincipalName
		);

		return result;
	};

	const contactMeansOfCallTypes = [
		{
			type: CallTargetType.Sip,
			key: contactListItemPropertyNames.userPrincipalName,
			icon: <Call20Regular />,
		},
		{
			type: CallTargetType.Bussiness,
			key: contactListItemPropertyNames.businessPhone,
			icon: <Phone20Regular />,
		},
		{
			type: CallTargetType.Mobile,
			key: contactListItemPropertyNames.mobilePhone,
			icon: <BookContacts20Regular />,
		},
	];

	const outboundCallMenuItems = useMemo<JSX.Element[]>(() => {
		const isDisabled =
			!isOutboundPossible ||
			!isOutboundAllowed ||
			!hasAnyOutboundRightEndpoints;
		const result = contactMeansOfCallTypes
			.filter((x) => !!contact[x.key])
			.map((x) => {
				const value = contact[x.key];
				return (
					<MenuItem
						key={`outbound-${x.key}`}
						icon={x.icon}
						title={value}
						className={menuItemClass.root}
						onClick={() => onCallClick(x.type, contact)}
						children={value}
						disabled={isDisabled}
					/>
				);
			});

		return result;
	}, [
		contact,
		isOutboundPossible,
		isOutboundAllowed,
		hasAnyOutboundRightEndpoints,
		contactMeansOfCallTypes,
		isAgendaEnabled,
		onCallClick,
	]);

	const transferEndpointMenuItems = (transferType: TransferType) => {
		const result = contactMeansOfCallTypes
			.filter((x) => !!contact[x.key])
			.map((x) => {
				const value = contact[x.key];
				return (
					<MenuItem
						key={`${transferType}-${x.key}`}
						icon={x.icon}
						title={value}
						className={menuItemClass.root}
						onClick={(_) => {
							setIsCallMenuOpened(false);
							onTransferClick(transferType, value);
						}}
						children={value}
					/>
				);
			});
		return result;
	};

	const contactTransferTypes = [
		{
			type: TransferType.AgentWarmCallTransfer,
			icon: <People20Regular />,
			disabled: !appSettings.callWarmTransferEnabled,
		},
		{
			type: TransferType.AgentColdCallTransfer,
			icon: <CallForward20Regular />,
			disabled: !appSettings.callColdTransferEnabled,
		},
	];

	const mapTypeTranslation = (type: TransferType) => {
		switch (type) {
			case TransferType.AgentWarmCallTransfer:
				return intl.formatMessage({ id: "Transfer.WarmTransfer" });
			case TransferType.AgentColdCallTransfer:
				return intl.formatMessage({ id: "Transfer.ColdTransfer" });
			default:
				return undefined;
		}
	};

	const transferMenuItems = useMemo<JSX.Element[]>(() => {
		const result = contactTransferTypes.map((x) => {
			const value = mapTypeTranslation(x.type);
			return (
				<MenuView
					trigger={
						<MenuItem
							key={crypto.randomUUID()}
							icon={x.icon}
							title={value}
							className={menuItemClass.root}
							children={value}
							disabled={x.disabled}
						/>
					}
					content={transferEndpointMenuItems(x.type)}
					positioning={{
						position: "before",
						offset: { mainAxis: 0, crossAxis: 0 },
					}}
				/>
			);
		});
		return result;
	}, [contact, contactTransferTypes, onTransferClick]);

	const agendaMenuItem = () => {
		const agentText = intl.formatMessage({ id: "Contacts.Menu.Agenda" });

		return (
			<MenuItem
				key={crypto.randomUUID()}
				icon={<Calendar20Regular />}
				title={agentText}
				className={menuItemClass.root}
				onClick={() => onAgendaClick(contact)}
				disabled={contact.contactType === ContactType.Database}
				children={agentText}
			/>
		);
	};

	const havingNoContactInformation =
		contactNotHavingCallableInformation(contact);

	const isCallButtonDisabled =
		(!!callSession &&
			callSessionStatesDisablingCallButton.includes(
				callSession.state as number
			)) ||
		(!callSession && !isOutboundPossible && !hasAnyOutboundRightEndpoints) ||
		havingNoContactInformation;

	const areTransferButtonsShown = useMemo<boolean>(() => {
		const result =
			!!callSession?.state &&
			callSessionStatesAllowingTransfer.includes(callSession.state);
		return result;
	}, [callSession]);

	const callMenuItems = useMemo<JSX.Element[]>(() => {
		const allMenuItems = areTransferButtonsShown
			? transferMenuItems
			: outboundCallMenuItems;

		return isAgendaEnabled ? [...allMenuItems, agendaMenuItem()] : allMenuItems;
	}, [contact, callSession, outboundCallMenuItems, transferMenuItems]);

	const titleTranslationId = havingNoContactInformation
		? areTransferButtonsShown
			? "Transfer.ContactMissingTransferProperties"
			: "Interaction.ContactMissingCallProperties"
		: "Interaction.Call";

	const title = intl.formatMessage({ id: titleTranslationId });

	return (
		<PopoverView
			trigger={
				<Button
					title={title}
					shape="circular"
					appearance="subtle"
					disabled={isCallButtonDisabled}
					icon={<Call20Regular />}
					className={classes.button}
					onClick={() => setIsCallMenuOpened(!isCallMenuOpened)}
				/>
			}
			content={<MenuList children={callMenuItems} />}
			positioning="above-start"
			classes={{ content: classes.menuPopoverClasses }}
			open={isCallMenuOpened ?? false}
			onOpenChange={handleOpenChange}
			renderOutsideTheDOM={true}
		/>
	);
};
